import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconX, IconChevronLeft, IconAlertTriangle } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import Breadcrumb from '../../components/common/Breadcrumb';
import MultiSelect from '../../components/common/MultiSelect';
import { useAuth } from '../../contexts/AuthContext';
import {
    useCreateGrantProposal,
    useUpdateGrantProposal,
    useTransitionGrantProposal,
    useGrantProposal,
    useGrantHistory,
} from '../../hooks/useGrants';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'GHS', 'KES', 'NGN', 'ZAR', 'TZS', 'UGX'];
const GRANT_TYPES = [
    { value: 'corporate',   label: 'Corporate' },
    { value: 'government',  label: 'Government' },
    { value: 'un',          label: 'UN / Multilateral' },
];
const PROJECT_DOMAIN_OPTIONS = [
    'Disease-Specific Programming',
    'Health Systems Strengthening',
    'Education & Literacy',
    'Water & Sanitation',
    'Food Security',
    'Gender & Inclusion',
    'Climate & Environment',
    'Economic Empowerment',
    'Digital Innovation',
].map(d => ({ id: d, name: d }));

const EMPTY_FORM = {
    title: '',
    description: '',
    client_donor: '',
    grant_type: '',
    project_domains: [],
    funding_amount: '',
    currency: 'USD',
    lead_person: '',
    deadline: '',
};

export default function SubmitProposal() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isRedraft = !!id;

    const { user } = useAuth();
    const orgId = user?.orgId;

    const createProposal = useCreateGrantProposal();
    const updateProposal = useUpdateGrantProposal();
    const transition = useTransitionGrantProposal();

    const { data: existingProposal } = useGrantProposal(id, orgId);
    const { data: history = [] } = useGrantHistory(id, orgId);

    const rejectionEntry = [...history].reverse().find(h => h.to_status === 'rejected');
    const rejectionComment = rejectionEntry?.comment || '';

    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (isRedraft && existingProposal) {
            setForm({
                title: existingProposal.title || '',
                description: existingProposal.description || '',
                client_donor: existingProposal.client_donor || '',
                grant_type: existingProposal.grant_type || '',
                project_domains: existingProposal.project_domains || [],
                funding_amount: existingProposal.funding_amount ?? '',
                currency: existingProposal.currency || 'USD',
                lead_person: existingProposal.lead_person || '',
                deadline: existingProposal.deadline || '',
            });
        }
    }, [isRedraft, existingProposal]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            funding_amount: parseFloat(form.funding_amount) || 0,
        };

        try {
            if (isRedraft) {
                await updateProposal.mutateAsync({ id, data: payload, orgId });
                await transition.mutateAsync({ id, data: { to_status: 'draft', comment: 'Re-drafted after rejection' }, orgId });
                toast.success('Proposal re-drafted successfully');
            } else {
                await createProposal.mutateAsync({ data: payload, orgId });
                toast.success('Proposal created successfully. You can now upload documents from the proposals list.');
            }
            navigate('/project-management/grant-proposal');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Something went wrong');
        }
    };

    const isPending = createProposal.isPending || updateProposal.isPending || transition.isPending;

    return (
        <>
            <Breadcrumb
                title={isRedraft ? 'Re-draft Proposal' : 'Submit Proposal'}
                path={`Project Management / Grant & Proposal / ${isRedraft ? 'Re-draft Proposal' : 'Submit Proposal'}`}
            />

            <div className='px-20'>
                <button
                    type='button'
                    onClick={() => navigate('/project-management/grant-proposal')}
                    className='btn btn-outline-secondary flex items-center gap-5 mb-15'
                >
                    <IconChevronLeft size={16} />
                    Back
                </button>
            </div>

            <div className='bg-bg-dark rounded-lg shadow-light p-20'>

                {isRedraft && rejectionComment && (
                    <div className='flex gap-10 bg-danger/10 border border-danger/30 rounded-lg px-15 py-12 mb-20'>
                        <IconAlertTriangle size={18} className='text-danger flex-shrink-0 mt-1' />
                        <div>
                            <p className='text-sm font-semibold text-danger mb-2'>Rejection Reason</p>
                            <p className='text-sm text-text-secondary'>{rejectionComment}</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className='flex flex-col gap-20'>

                    {/* Title */}
                    <div>
                        <label className='block text-sm font-medium mb-6'>Title <span className='text-danger'>*</span></label>
                        <input
                            type='text' name='title' value={form.title} onChange={handleChange} required
                            placeholder='Name of the proposaln'
                            className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg placeholder:text-text-secondary focus:outline-none focus:border-primary transition-colors'
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className='block text-sm font-medium mb-6'>Description <span className='text-danger'>*</span></label>
                        <textarea
                            name='description' value={form.description} onChange={handleChange} required rows={5}
                            placeholder='Detailed overview of the project...'
                            className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg placeholder:text-text-secondary focus:outline-none focus:border-primary transition-colors resize-none'
                        />
                    </div>

                    {/* Donor & Grant Type */}
                    <div className='grid md:grid-cols-2 gap-20'>
                        <div>
                            <label className='block text-sm font-medium mb-6'>Donor / Organization <span className='text-danger'>*</span></label>
                            <input
                                type='text' name='client_donor' value={form.client_donor} onChange={handleChange} required
                                placeholder='Funding entity or organization'
                                className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg placeholder:text-text-secondary focus:outline-none focus:border-primary transition-colors'
                            />
                        </div>
                        <div>
                            <label className='block text-sm font-medium mb-6'>Grant Type <span className='text-danger'>*</span></label>
                            <select
                                name='grant_type' value={form.grant_type} onChange={handleChange} required
                                className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg focus:outline-none focus:border-primary transition-colors'
                            >
                                <option value=''>Select grant type</option>
                                {GRANT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Amount & Currency */}
                    <div className='grid md:grid-cols-2 gap-20'>
                        <div>
                            <label className='block text-sm font-medium mb-6'>Amount & Currency <span className='text-danger'>*</span></label>
                            <div className='flex gap-10'>
                                <select
                                    name='currency' value={form.currency} onChange={handleChange}
                                    className='bg-bg-light border border-border py-[10px] px-15 rounded-lg focus:outline-none focus:border-primary transition-colors w-[110px] flex-shrink-0'
                                >
                                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <input
                                    type='number' name='funding_amount' value={form.funding_amount} onChange={handleChange} required min='0' placeholder='0.00'
                                    className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg placeholder:text-text-secondary focus:outline-none focus:border-primary transition-colors'
                                />
                            </div>
                        </div>
                        <div>
                            <label className='block text-sm font-medium mb-6'>Lead Person <span className='text-danger'>*</span></label>
                            <input
                                type='text' name='lead_person' value={form.lead_person} onChange={handleChange} required
                                placeholder='Name of lead person'
                                className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg placeholder:text-text-secondary focus:outline-none focus:border-primary transition-colors'
                            />
                        </div>
                    </div>

                    {/* Deadline */}
                    <div className='grid md:grid-cols-2 gap-20'>
                        <div>
                            <label className='block text-sm font-medium mb-6'>Deadline</label>
                            <input
                                type='date' name='deadline' value={form.deadline} onChange={handleChange}
                                className='bg-bg-light border border-border w-full py-[10px] px-15 rounded-lg focus:outline-none focus:border-primary transition-colors'
                            />
                        </div>
                    </div>

                    {/* Project Domains */}
                    <div>
                        <label className='block text-sm font-medium mb-6'>Project Domains</label>
                        <MultiSelect
                            options={PROJECT_DOMAIN_OPTIONS}
                            selectedIds={form.project_domains}
                            onChange={(selected) => setForm(prev => ({ ...prev, project_domains: selected }))}
                            placeholder='Select project domains...'
                        />
                    </div>

                    {/* Actions */}
                    <div className='flex gap-10 justify-end pt-10 border-t border-border'>
                        <button type='button' onClick={() => navigate('/project-management/grant-proposal')}
                            className='btn btn-outline-secondary'>
                            Cancel
                        </button>
                        <button type='submit' className='btn btn-primary' disabled={isPending}>
                            {isPending
                                ? (isRedraft ? 'Saving...' : 'Submitting...')
                                : (isRedraft ? 'Re-draft Proposal' : 'Submit Proposal')
                            }
                        </button>
                    </div>
                </form>
            </div>

        </>
    );
}
